// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import React from 'react';
import {
  Box,
  Button,
  Link,
  SpaceBetween,
  TextContent,
} from '@cloudscape-design/components';
import { useHistory } from 'react-router-dom';
import { ACCOUNTS, IMPORT } from '../../routes';

const CliVerification = () => (
  <SpaceBetween size="m">
    <TextContent>
      <h3>Verify CLI Onboarding</h3>
      <p>After running the CLI onboarding command:</p>
      <ol>
        <li>
          Navigate to the accounts page to check your account&apos;s status
        </li>
        <li>
          Your account should appear and complete processing within 5 minutes
        </li>
        <li>If your account does not appear, try re-running the onboard command</li>
      </ol>
    </TextContent>

    <TextContent>
      <h3>Troubleshooting</h3>
      <ul>
        <li>
          Ensure you are authenticated with <code>mwinit</code> and ADA is
          properly configured
        </li>
        <li>
          Verify your AWS credentials have sufficient permissions
        </li>
        <li>
          Try running <code>toolbox update resourcelens</code> to ensure you
          have the latest version
        </li>
      </ul>
    </TextContent>
  </SpaceBetween>
);

const PersistentVerification = () => {
  const history = useHistory();

  return (
    <SpaceBetween size="m">
      <TextContent>
        <h3>Verify Persistent Onboarding</h3>
        <p>
          After deploying ResourceLens resources, verify the deployment was
          successful:
        </p>

        <h4>Check IAM Roles</h4>
        <ul>
          <li>Navigate to IAM → Roles in the AWS Console</li>
          <li>
            Confirm these roles exist:
            <ul>
              <li>
                <code>WorkloadDiscoveryRole</code>
              </li>
              <li>
                <code>WorkloadDiscoveryMyApplicationsRole</code>
              </li>
            </ul>
          </li>
        </ul>

        <h4>Check AWS Config</h4>
        <ul>
          <li>Navigate to AWS Config in each target region</li>
          <li>Confirm Config is enabled and recording</li>
          <li>Verify AggregationAuthorization is configured</li>
        </ul>
      </TextContent>

      <TextContent>
        <h3>Import Accounts</h3>
        <p>Once deployment is verified, import your accounts:</p>
        <ol>
          <li>
            Navigate to the{' '}
            <Link
              href={IMPORT}
              onFollow={(e) => {
                e.preventDefault();
                history.push(IMPORT);
              }}
            >
              Import page
            </Link>
          </li>
          <li>Select the accounts and regions you deployed ResourceLens to</li>
          <li>Confirm the deployment checkboxes</li>
          <li>Click Import to begin discovery</li>
        </ol>
      </TextContent>

      <TextContent>
        <h3>Troubleshooting</h3>
        <ul>
          <li>
            <strong>Permission errors:</strong> Verify your AWS credentials have
            sufficient permissions
          </li>
          <li>
            <strong>Role creation failures:</strong> Check for existing roles
            with similar names
          </li>
          <li>
            <strong>Config issues:</strong> Ensure AWS Config is supported in
            your target regions
          </li>
          <li>
            <strong>Import failures:</strong> Verify roles were created
            successfully before importing
          </li>
        </ul>
      </TextContent>
    </SpaceBetween>
  );
};

const VerificationStep = ({ method }) => {
  const history = useHistory();

  return (
    <SpaceBetween size="l">
      {method === 'cli' ? (
        <CliVerification />
      ) : (
        <PersistentVerification />
      )}

      <Box>
        <Button
          variant="primary"
          onClick={() => history.push(ACCOUNTS)}
        >
          Check Onboarding Status
        </Button>
      </Box>
    </SpaceBetween>
  );
};

export default VerificationStep;
